/**
 * 품목 검색 기능이 포함된 상세 테이블 이벤트 핸들러 훅
 * 품목 코드 더블클릭으로 품목 검색 모달을 여는 기능 제공
 */

import { useCallback, useMemo } from "react";
import { CreateCellDoubleClickHandler, CustomCellDoubleClickAction } from "src/utils/grid/GridHandlerContent";
import { showWarning } from "src/utils/toast/toastUtil";
import { 
	ItemDetailDto, 
	ItemSearchDetailTableEventsProps, 
	ItemSearchHandlers 
} from "src/types/detail-table-new";
import { useDetailTableEvents } from "./useDetailTableEvents";

export function useDetailTableWithItemSearch<T extends ItemDetailDto>({
	detailList,
	editableColumns = [],
	itemSearchConfig,
	constraints,
	keyDownConfig,
}: ItemSearchDetailTableEventsProps<T>): ItemSearchHandlers<T> {

	// 기본 핸들러들 가져오기
	const basicHandlers = useDetailTableEvents({
		detailList,
		editableColumns,
		keyDownConfig,
	});

	/**
	 * 품목 검색 더블클릭 핸들러 생성
	 */
	const cellDoubleClickActions: CustomCellDoubleClickAction<T>[] = useMemo(() => {
		const actions: CustomCellDoubleClickAction<T>[] = [];

		// 제약 조건 체크 함수 (foundData 파라미터 추가)
		const isActionAllowed = (foundData?: T): boolean => {

			if (constraints?.notTransport === false) {
				showWarning('출하정보가 있는 데이터는 수정이 불가능합니다.');
				return false;
			}

			// notEdit 을 받아와서 순수 boolean인지 boolean 반환 함수인지 탐지 (boolean 또는 function)
			// 순수 boolean든 boolean 반환 함수든 값이 false면 편집 불가.
			const notEditConstraint = constraints?.notEdit;

			let isNotEditable = false;

			if (typeof notEditConstraint === 'function') {
				// 함수인 경우 실행. params 형태로 각 row의 데이터 전달
				isNotEditable = notEditConstraint({ data: foundData });
			} else if (typeof notEditConstraint === 'boolean') {
				// boolean인 경우 값 사용
				isNotEditable = notEditConstraint;
			}

			if (isNotEditable === false) {
				showWarning('편집이 불가능한 상태입니다.');
				return false;
			}
			return true;
		};

		const config = itemSearchConfig.config;

		// itemCd 필드 더블클릭 (기존 값으로 검색)
		actions.push({
			validate: (field) => field === 'itemCd',
			action: (e, foundData) => {
				if (!isActionAllowed(foundData)) return; // foundData 전달

				if (itemSearchConfig.settingCodeSearchModalProps) {
					itemSearchConfig.settingCodeSearchModalProps(
						config.category,
						(foundData as any).itemCd || "",
						false,
						'itemCd',
						foundData,
						foundData.valueIndex
					);
				}
			}
		});

		// 관련 필드들 더블클릭 (빈 값으로 검색)
		Object.values(config.responseMapping).forEach(targetField => {
			if (targetField !== 'itemCd') {
				actions.push({
					validate: (field) => field === targetField,
					action: (e, foundData) => {
						if (!isActionAllowed(foundData)) return; // foundData 전달

						if (itemSearchConfig.settingCodeSearchModalProps) {
							itemSearchConfig.settingCodeSearchModalProps(
								config.category,
								"",
								false,
								'itemCd',
								foundData,
								foundData.valueIndex
							);
						}
					}
				});
			}
		});

		return actions;
	}, [itemSearchConfig, constraints]);

	const handleCellDoubleClick = CreateCellDoubleClickHandler(detailList, 'valueIndex', cellDoubleClickActions);

	// 키다운 핸들러 확장 (품목 검색 특화)
	const enhancedHandleCellKeyDown = useCallback((event: any) => {
		// 품목 검색 특화 키다운 로직 (엔터 키 처리를 먼저)
		if (event.event.key === 'Tab' && event.colDef.field === 'itemCd') {
			// 제약 조건 체크
			if (constraints?.notTransport === false) {
				showWarning('출하정보가 있는 데이터는 수정이 불가능합니다.');
				return;
			}

			//  AG-Grid의 node data를 직접 체크하여 최신 값 확인
			const api = event.api;
			const node = api.getRowNode(event.node.id);
			const qtyField = keyDownConfig?.qtyField || 'orderQty';

			// setTimeout으로 AG-Grid의 내부 값 업데이트 대기
			setTimeout(() => {
				const latestData = node?.data;
				if (!latestData) return;

				const currentItemCd = latestData.itemCd;
				const currentItemNm = latestData.itemNm;

				// itemCd는 있지만 itemNm이 없으면 자동완성 실패 → 모달 열기
				// itemCd도 없으면 모달 열기
				if ((!currentItemCd || currentItemCd.trim() === '') || 
					(currentItemCd && (!currentItemNm || currentItemNm.trim() === ''))) {
					if (itemSearchConfig.settingCodeSearchModalProps) {
						itemSearchConfig.settingCodeSearchModalProps(
							itemSearchConfig.config.category,
							currentItemCd || "",
							false,
							'itemCd',
							latestData,
							latestData.valueIndex
						);
					}
				} else {
					// 자동완성 성공 시 다음 셀(수량)로 포커스 이동
					api.setFocusedCell(event.rowIndex, qtyField);
					api.startEditingCell({
						rowIndex: event.rowIndex,
						colKey: qtyField,
					});
				}
			}, 150); // AG-Grid 내부 처리 대기

			// 탭 키 기본 동작 방지 (수동으로 포커스 이동 처리)
			event.event.preventDefault();
			return;
		}

		// 기본 키다운 핸들러 실행 (엔터 키, Ctrl+C 이외의 경우)
		basicHandlers.handleCellKeyDown(event);
	}, [basicHandlers.handleCellKeyDown, detailList, itemSearchConfig, constraints, keyDownConfig
		//, copyToClipboard
	]);

	return {
		...basicHandlers,
		handleCellKeyDown: enhancedHandleCellKeyDown,
		handleCellDoubleClick,
	};
}
